import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  LivenessWebVerifyStatusEnum,
  RegtankOnboardingStatusEnum,
  EntityTypeEnum,
} from "@funded-here-interface/common/src/constant/enum";

interface OrgDirectorType {
  firstName?: string;
  lastName?: string;
  email?: string;
  spvBoardResolutionSigned?: boolean;
  isAuthRep?: boolean;
  isShareholder?: boolean;
  userId?: number;
  orgRoleId?: number;
}

interface Spv {
  companyName?: string;
  legalEntityAddress?: string;
  companyRegistrationNo?: string;
  legalStructure?: string;
  industry?: string;
  countryISOCode?: string;
  countryOfIncorporation?: string;
  companyPhoneNumber?: string;
  nameOfAuthorisedRepresentiative?: string;
  postalCode?: number;
  titleOfAuthorisedRepresentiative?: string;
  emailOfAuthorisedRepresentiative?: string;
  authRepIsMerchant?: boolean;
  orgRoleIdOfAuthorisedRepresentiative?: number;
  website?: string;
  boardOfDirectors?: OrgDirectorType[];
  kycStatus?: string;
  createdAt?: Date;
}

export interface regtankLivenessCheck {
  id?: number | string;
  sessionId: string;
  livenessStatus: LivenessWebVerifyStatusEnum | null;
  clientSessionId: string;
  regtankLivenessUrl?: string | null;
  noOfAttemptLiveness: number;
  reattemptApproval: string | null;
  expiredDate: string;
  reason?: string;
  createdAt?: string;
  confidence?: number;
  agreementName?: string;
  remark?: string;
  reviewedDate?: string;
  submittedDate?: string;
  metaData?: Record<string, any> | null;
}

interface regtankRequest {
  requestId: string;
  onboardingStatus: RegtankOnboardingStatusEnum | null;
  expiredDate: string;
}

interface AuthState {
  token: string;
  userId: string;
  spvStatus: string | null;
  type: string | null;
  userEmail: string | null;
  role: string | null;
  orgId: number | null;
  orgRoleId: number | null;
  userFirstName: string | null;
  userLastName: string | null;
  agreedToMembershipAgreement: boolean | null;
  agreedToSpvMembershipAgreement: boolean | null;
  isAuthRep: boolean | null;
  hasCreditLimit: boolean | null;
  entityType: EntityTypeEnum | null;
  isAdminAuthRep: boolean | null;
  spvName: string | null;
  spvDetails: Partial<Spv> | null;
  orgName: string | null;
  regtankRequest: regtankRequest[] | null;
  regtankLiveness: regtankLivenessCheck[] | null;
  regtankOnboardingFeatureFlag: boolean | null;
  regtankLivenessFeatureFlag: boolean | null;
  regtankOnboarding: boolean;
  kycStatus?: string | null;
  isSuspended?: boolean | null;
  declarationAISubmitted: boolean;
  investorChecklist?: Record<string, any> | null;
  hasEmailHistory: boolean;
  hasSignedDocument?: boolean;
  adminOrgRoleId: number | null;
  onboardingModalDismissed: boolean;
  hasSeenEkycCompleted: boolean;
  hasSeenSignedAgreement: boolean;
}

const initialState: AuthState = {
  token: "none",
  userId: "",
  spvStatus: null,
  type: null,
  userEmail: null,
  role: null,
  orgId: null,
  orgRoleId: null,
  userFirstName: null,
  userLastName: null,
  agreedToMembershipAgreement: null,
  agreedToSpvMembershipAgreement: null,
  isAuthRep: null,
  hasCreditLimit: null,
  entityType: null,
  isAdminAuthRep: null,
  spvName: null,
  spvDetails: null,
  orgName: null,
  regtankRequest: null,
  regtankLiveness: null,
  regtankOnboardingFeatureFlag: null,
  regtankLivenessFeatureFlag: null,
  regtankOnboarding: false,
  isSuspended: false,
  declarationAISubmitted: false,
  investorChecklist: null,
  hasEmailHistory: false,
  hasSignedDocument: false,
  adminOrgRoleId: null,
  onboardingModalDismissed: false,
  hasSeenEkycCompleted: false,
  hasSeenSignedAgreement: false,
};

interface HydrateInvestorAuthPayload {
  kycStatus?: string | null;
  isSuspended?: boolean | null;
  agreedToMembershipAgreement?: boolean | null;
  orgName?: string | null;
  agreedToSpvMembershipAgreement?: boolean | null;
  hasCreditLimit?: boolean | null;
  isAuthRep?: boolean | null;
  isAdminAuthRep?: boolean | null;
  entityType?: EntityTypeEnum | string | null;
  userId?: string | number;
  userEmail?: string | null;
  spvStatus?: string | null;
  spvName?: string | null;
  pendingOnBoardingAs?: string | null;
  role?: string | null;
  organization?: number | string | null;
  currentOrgRoleId?: number | string | null;
  firstName?: string | null;
  lastName?: string | null;
  spvDetails?: Partial<Spv> | null;
  regtankOnboardingFeatureFlag?: boolean | null;
  regtankLivenessFeatureFlag?: boolean | null;
  regtankRequest?: regtankRequest[] | null;
  regtankLiveness?: regtankLivenessCheck[] | null;
  regtankOnboarding?: boolean;
  declarationAISubmitted?: boolean;
  hasEmailHistory?: boolean;
  hasSignedDocument?: boolean;
  adminOrgRoleId?: number | null;
  onboardingModalDismissed?: boolean;
  hasSeenEkycCompleted?: boolean;
  investorChecklist?: Record<string, any> | null;
  hasSeenSignedAgreement?: boolean;
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setInvestorToken: (state, action: PayloadAction<{ token: string }>) => {
      state.token = action.payload.token;
    },
    setUserID: (state, action: PayloadAction<{ userId: string }>) => {
      state.userId = action.payload.userId;
    },
    setUserSpv: (
      state,
      action: PayloadAction<{ spvStatus: string | null }>,
    ) => {
      state.spvStatus = action.payload.spvStatus;
    },
    setOnboardingType: (
      state,
      action: PayloadAction<{ type: string | null }>,
    ) => {
      state.type = action.payload.type;
    },
    setUserEmail: (
      state,
      action: PayloadAction<{ userEmail: string | null }>,
    ) => {
      state.userEmail = action.payload.userEmail;
    },
    setRole: (state, action: PayloadAction<{ role: string | null }>) => {
      state.role = action.payload.role;
    },
    setOrgId: (state, action: PayloadAction<{ orgId: number | null }>) => {
      state.orgId = action.payload.orgId;
    },
    setOrgRoleId: (
      state,
      action: PayloadAction<{ currentOrgRoleId: number | null }>,
    ) => {
      state.orgRoleId = action.payload.currentOrgRoleId;
    },
    setUserFirstName: (
      state,
      action: PayloadAction<{ userFirstName: string | null }>,
    ) => {
      state.userFirstName = action.payload.userFirstName;
    },
    setUserLastName: (
      state,
      action: PayloadAction<{ userLastName: string | null }>,
    ) => {
      state.userLastName = action.payload.userLastName;
    },
    setAgreedToMembershipAgreement: (
      state,
      action: PayloadAction<{ agreedToMembershipAgreement: boolean | null }>,
    ) => {
      state.agreedToMembershipAgreement =
        action.payload.agreedToMembershipAgreement;
    },
    setAgreedToSpvMembershipAgreement: (
      state,
      action: PayloadAction<{ agreedToSpvMembershipAgreement: boolean | null }>,
    ) => {
      state.agreedToSpvMembershipAgreement =
        action.payload.agreedToSpvMembershipAgreement;
    },
    setIsAuthRep: (
      state,
      action: PayloadAction<{ isAuthRep: boolean | null }>,
    ) => {
      state.isAuthRep = action.payload.isAuthRep;
    },
    setHasCreditLimit: (
      state,
      action: PayloadAction<{ hasCreditLimit: boolean | null }>,
    ) => {
      state.hasCreditLimit = action.payload.hasCreditLimit;
    },
    setEntityType: (
      state,
      action: PayloadAction<{ entityType: EntityTypeEnum | null }>,
    ) => {
      state.entityType = action.payload.entityType;
    },
    setIsAdminAuthRep: (
      state,
      action: PayloadAction<{ isAdminAuthRep: boolean | null }>,
    ) => {
      state.isAdminAuthRep = action.payload.isAdminAuthRep;
    },
    setSpvName: (state, action: PayloadAction<{ spvName: string | null }>) => {
      state.spvName = action.payload.spvName;
    },
    setKycStatus: (
      state,
      action: PayloadAction<{ kycStatus: string | null }>,
    ) => {
      state.kycStatus = action.payload.kycStatus;
    },
    setSpvDetails: (
      state,
      action: PayloadAction<{ spvDetails: Spv | null }>,
    ) => {
      state.spvDetails = action.payload.spvDetails;
    },
    setOrgName: (state, action: PayloadAction<{ orgName: string | null }>) => {
      state.orgName = action.payload.orgName;
    },
    setRegtankRequest: (
      state,
      action: PayloadAction<{ regtankRequest: regtankRequest[] | null }>,
    ) => {
      state.regtankRequest = action.payload.regtankRequest;
    },
    setRegtankLiveness: (
      state,
      action: PayloadAction<{ regtankLiveness: regtankLivenessCheck[] | null }>,
    ) => {
      state.regtankLiveness = action.payload.regtankLiveness;
    },
    setRegtankOnboardingFlag: (
      state,
      action: PayloadAction<{ regtankOnboardingFeatureFlag: boolean | null }>,
    ) => {
      state.regtankOnboardingFeatureFlag =
        action.payload.regtankOnboardingFeatureFlag;
    },
    setRegtankLivenessFlag: (
      state,
      action: PayloadAction<{ regtankLivenessFeatureFlag: boolean | null }>,
    ) => {
      state.regtankLivenessFeatureFlag =
        action.payload.regtankLivenessFeatureFlag;
    },
    setRegtankOnboarding: (
      state,
      action: PayloadAction<{ regtankOnboarding: boolean }>,
    ) => {
      state.regtankOnboarding = action.payload.regtankOnboarding;
    },
    setIsSuspended: (
      state,
      action: PayloadAction<{ isSuspended: boolean | null }>,
    ) => {
      state.isSuspended = action.payload.isSuspended;
    },
    setDeclarationAISubmitted: (
      state,
      action: PayloadAction<{ declarationAISubmitted: boolean }>,
    ) => {
      state.declarationAISubmitted = action.payload.declarationAISubmitted;
    },
    setAdminOrgRoleId: (
      state,
      action: PayloadAction<{ adminOrgRoleId: number | null }>,
    ) => {
      state.adminOrgRoleId = action.payload.adminOrgRoleId;
    },
    setOnboardingModalDismissed: (
      state,
      action: PayloadAction<{ onboardingModalDismissed: boolean }>,
    ) => {
      state.onboardingModalDismissed = action.payload.onboardingModalDismissed;
    },
    setHasSeenEkycCompleted: (
      state,
      action: PayloadAction<{ hasSeenEkycCompleted: boolean }>,
    ) => {
      state.hasSeenEkycCompleted = action.payload.hasSeenEkycCompleted;
    },
    setHasSeenSignedAgreement: (
      state,
      action: PayloadAction<{ hasSeenSignedAgreement: boolean }>,
    ) => {
      state.hasSeenSignedAgreement = action.payload.hasSeenSignedAgreement;
    },
    /**
     * Bulk hydrate investor auth state after login/2FA
     *
     * Use this for: Auth hydration after login, bulk state initialization
     * Use individual setters for: Incremental updates to specific fields
     */
    hydrateInvestorAuth: (
      state,
      action: PayloadAction<HydrateInvestorAuthPayload>,
    ) => {
      const {
        kycStatus,
        isSuspended,
        agreedToMembershipAgreement,
        orgName,
        agreedToSpvMembershipAgreement,
        hasCreditLimit,
        isAuthRep,
        isAdminAuthRep,
        entityType,
        userId,
        userEmail,
        spvStatus,
        spvName,
        pendingOnBoardingAs,
        role,
        organization,
        currentOrgRoleId,
        firstName,
        lastName,
        spvDetails,
        regtankOnboardingFeatureFlag,
        regtankLivenessFeatureFlag,
        regtankRequest,
        regtankLiveness,
        regtankOnboarding,
        declarationAISubmitted,
        investorChecklist,
        hasEmailHistory,
        hasSignedDocument,
        adminOrgRoleId,
        onboardingModalDismissed,
        hasSeenEkycCompleted,
        hasSeenSignedAgreement,
      } = action.payload;

      // Single bulk state update - Redux Toolkit's Immer handles immutability
      // Use !== undefined consistently so null from API can clear fields
      state.kycStatus = kycStatus !== undefined ? kycStatus : state.kycStatus;
      state.isSuspended =
        isSuspended !== undefined ? isSuspended : state.isSuspended;
      state.agreedToMembershipAgreement =
        agreedToMembershipAgreement !== undefined
          ? agreedToMembershipAgreement
          : state.agreedToMembershipAgreement;
      state.orgName = orgName !== undefined ? orgName : state.orgName;
      state.agreedToSpvMembershipAgreement =
        agreedToSpvMembershipAgreement !== undefined
          ? agreedToSpvMembershipAgreement
          : state.agreedToSpvMembershipAgreement;
      state.hasCreditLimit =
        hasCreditLimit !== undefined ? hasCreditLimit : state.hasCreditLimit;
      state.isAuthRep = isAuthRep !== undefined ? isAuthRep : state.isAuthRep;
      state.isAdminAuthRep =
        isAdminAuthRep !== undefined ? isAdminAuthRep : state.isAdminAuthRep;
      state.entityType =
        entityType !== undefined
          ? (entityType as EntityTypeEnum | null)
          : state.entityType;
      state.userId = userId != null ? String(userId) : state.userId;
      state.userEmail = userEmail !== undefined ? userEmail : state.userEmail;
      state.spvStatus = spvStatus !== undefined ? spvStatus : state.spvStatus;
      state.spvName = spvName !== undefined ? spvName : state.spvName;
      state.type =
        pendingOnBoardingAs !== undefined ? pendingOnBoardingAs : state.type;
      state.role = role !== undefined ? role : state.role;
      state.orgId = organization != null ? Number(organization) : state.orgId;
      state.orgRoleId =
        currentOrgRoleId != null ? Number(currentOrgRoleId) : state.orgRoleId;
      state.userFirstName =
        firstName !== undefined ? firstName : state.userFirstName;
      state.userLastName =
        lastName !== undefined ? lastName : state.userLastName;
      state.spvDetails =
        spvDetails !== undefined ? spvDetails : state.spvDetails;
      state.regtankOnboardingFeatureFlag =
        regtankOnboardingFeatureFlag !== undefined
          ? regtankOnboardingFeatureFlag
          : state.regtankOnboardingFeatureFlag;
      state.regtankLivenessFeatureFlag =
        regtankLivenessFeatureFlag !== undefined
          ? regtankLivenessFeatureFlag
          : state.regtankLivenessFeatureFlag;
      state.regtankRequest =
        regtankRequest !== undefined ? regtankRequest : state.regtankRequest;
      state.regtankLiveness =
        regtankLiveness !== undefined ? regtankLiveness : state.regtankLiveness;
      state.regtankOnboarding =
        regtankOnboarding !== undefined
          ? regtankOnboarding
          : state.regtankOnboarding;
      state.declarationAISubmitted =
        declarationAISubmitted !== undefined
          ? declarationAISubmitted
          : state.declarationAISubmitted;
      state.investorChecklist =
        investorChecklist !== undefined
          ? investorChecklist
          : state.investorChecklist;
      state.hasEmailHistory =
        hasEmailHistory !== undefined ? hasEmailHistory : state.hasEmailHistory;
      state.hasSignedDocument =
        hasSignedDocument !== undefined
          ? hasSignedDocument
          : state.hasSignedDocument;
      state.adminOrgRoleId =
        adminOrgRoleId !== undefined ? adminOrgRoleId : state.adminOrgRoleId;
      state.onboardingModalDismissed =
        onboardingModalDismissed !== undefined
          ? onboardingModalDismissed
          : state.onboardingModalDismissed;
      state.hasSeenEkycCompleted =
        hasSeenEkycCompleted !== undefined
          ? hasSeenEkycCompleted
          : state.hasSeenEkycCompleted;
      state.hasSeenSignedAgreement =
        hasSeenSignedAgreement !== undefined
          ? hasSeenSignedAgreement
          : state.hasSeenSignedAgreement;
    },
    resetAuthState: () => initialState,
  },
});

export const {
  setInvestorToken,
  setUserID,
  setUserSpv,
  setOnboardingType,
  setUserEmail,
  setRole,
  setOrgId,
  setOrgRoleId,
  setUserFirstName,
  setUserLastName,
  setAgreedToMembershipAgreement,
  setAgreedToSpvMembershipAgreement,
  setIsAuthRep,
  setHasCreditLimit,
  setEntityType,
  setIsAdminAuthRep,
  setSpvName,
  resetAuthState,
  setSpvDetails,
  setOrgName,
  setRegtankRequest,
  setRegtankLiveness,
  setRegtankLivenessFlag,
  setRegtankOnboardingFlag,
  setRegtankOnboarding,
  setKycStatus,
  setIsSuspended,
  setDeclarationAISubmitted,
  setAdminOrgRoleId,
  setOnboardingModalDismissed,
  setHasSeenEkycCompleted,
  setHasSeenSignedAgreement,
  hydrateInvestorAuth, // Bulk auth hydration - replaces 22 individual dispatches
} = authSlice.actions;
export default authSlice.reducer;
